/**
 * Lightweight dominant-color extractor for an image URL.
 * Uses canvas to sample pixels and find the most representative color.
 * Falls back to a default if the image fails CORS or load.
 */

export const extractDominantColor = (imageUrl, fallback = '#D4AF37') => new Promise((resolve) => {
  if (!imageUrl) return resolve(fallback);
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const size = 64;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, size, size);
      const { data } = ctx.getImageData(0, 0, size, size);
      // Bucket colors into a 5x5x5 grid to find the dominant non-greyish color
      const buckets = {};
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
        if (a < 128) continue; // transparent
        // Skip near-black and near-white for better brand color extraction
        const brightness = (r + g + b) / 3;
        if (brightness < 25 || brightness > 240) continue;
        // Skip greys
        const maxC = Math.max(r, g, b), minC = Math.min(r, g, b);
        if (maxC - minC < 15) continue;
        const key = `${Math.round(r / 51)}-${Math.round(g / 51)}-${Math.round(b / 51)}`;
        if (!buckets[key]) buckets[key] = { count: 0, r: 0, g: 0, b: 0 };
        buckets[key].count++;
        buckets[key].r += r;
        buckets[key].g += g;
        buckets[key].b += b;
      }
      let bestKey = null;
      let bestCount = 0;
      Object.entries(buckets).forEach(([k, v]) => {
        if (v.count > bestCount) { bestCount = v.count; bestKey = k; }
      });
      if (!bestKey) return resolve(fallback);
      const b = buckets[bestKey];
      const r = Math.round(b.r / b.count);
      const g = Math.round(b.g / b.count);
      const bl = Math.round(b.b / b.count);
      const hex = '#' + [r, g, bl].map(x => x.toString(16).padStart(2, '0')).join('');
      resolve(hex);
    } catch (_) {
      resolve(fallback);
    }
  };
  img.onerror = () => resolve(fallback);
  img.src = imageUrl;
});

/** Return a contrasting text color (#000 or #fff) for a given background hex. */
export const getContrastColor = (hex) => {
  if (!hex || hex.length < 4) return '#fff';
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.55 ? '#000' : '#fff';
};
